/**
 * UniversalRow Component
 *
 * A truly "ignorant" row component that doesn't know what it's rendering.
 * It simply has SLOTS that get filled or remain empty based on the data passed.
 *
 * This is the foundation for ALL list items in the platform:
 * - Tasks, Calls, Emails, Events, Alerts, Notifications, etc.
 * - The same component, different data
 *
 * Layout with all slots:
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ [left] [icon] [content-area]                    [date] [status] [right] │
 * │        │      ├─ title (required)                │      🟢🟡🔴   │      │
 * │        │      ├─ subtitle (opt)                  │               │      │
 * │        │      └─ meta (opt)                      │               │      │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Empty slots simply don't render - no space taken.
 */

import React, { useState, useRef, useEffect, useMemo, type ReactNode, type CSSProperties } from 'react';
import { Zap } from 'lucide-react';
import { useTranslation } from '../../../hooks';

// =============================================================================
// TYPES
// =============================================================================

export type StatusLevel = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export interface UniversalRowProps {
  // === REQUIRED ===
  /** Unique identifier */
  id: string;
  /** Main text - always displayed */
  title: string;

  // === OPTIONAL SLOTS ===
  /** Left slot - checkbox, selection indicator, custom element */
  leftSlot?: ReactNode;
  /** Icon slot - entity type icon, direction arrow, etc. */
  icon?: ReactNode;
  /** Subtitle - secondary text line */
  subtitle?: string;
  /** Meta content - tags, badges, extra info (ReactNode for flexibility) */
  meta?: ReactNode;
  /** Date display - auto-formatted if string/Date, or custom ReactNode */
  date?: string | Date | ReactNode;
  /** Status indicator dot */
  status?: StatusLevel;
  /** Right slot - fast actions, timer, custom buttons */
  rightSlot?: ReactNode;

  // === STATE ===
  /** Mark as completed/done - applies visual treatment */
  done?: boolean;
  /** Currently selected/active row */
  selected?: boolean;
  /** Disabled state */
  disabled?: boolean;

  // === STYLE VARIANTS ===
  /** Size variant */
  size?: 'compact' | 'default' | 'comfortable';
  /** Visual emphasis */
  emphasis?: 'default' | 'subtle' | 'highlighted';

  // === HANDLERS ===
  /** Click on the row (not on specific slots) */
  onClick?: () => void;
  /** Click on title specifically (for navigation) */
  onTitleClick?: () => void;
  /** Double click */
  onDoubleClick?: () => void;

  // === STYLING ===
  className?: string;
  style?: CSSProperties;
}

// =============================================================================
// STATUS COLORS
// =============================================================================

const STATUS_COLORS: Record<StatusLevel, string> = {
  success: '#22c55e',  // green
  warning: '#f59e0b',  // amber
  danger: '#ef4444',   // red
  info: '#3b82f6',     // blue
  neutral: '#9ca3af',  // gray
};

// =============================================================================
// SIZE VARIANTS
// =============================================================================

const SIZE_CONFIG = {
  compact: {
    minHeight: '32px',
    padding: '4px 10px',
    fontSize: '12px',
    iconSize: 14,
    gap: '6px',
  },
  default: {
    minHeight: '40px',
    padding: '6px 12px',
    fontSize: '13px',
    iconSize: 16,
    gap: '8px',
  },
  comfortable: {
    minHeight: '52px',
    padding: '10px 16px',
    fontSize: '14px',
    iconSize: 18,
    gap: '10px',
  },
};

// =============================================================================
// COMPONENT
// =============================================================================

export function UniversalRow({
  id,
  title,
  leftSlot,
  icon,
  subtitle,
  meta,
  date,
  status,
  rightSlot,
  done = false,
  selected = false,
  disabled = false,
  size = 'default',
  emphasis = 'default',
  onClick,
  onTitleClick,
  onDoubleClick,
  className = '',
  style,
}: UniversalRowProps) {
  const [hovered, setHovered] = useState(false);
  const sizeConfig = SIZE_CONFIG[size];

  // Format date if needed
  const formattedDate = formatDate(date);

  // Compute row styles based on state
  const rowStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    minHeight: sizeConfig.minHeight,
    padding: sizeConfig.padding,
    gap: sizeConfig.gap,
    fontSize: sizeConfig.fontSize,
    borderBottom: '1px solid var(--line, #e5e7eb)',
    background: getBackground(emphasis, selected, hovered, done, disabled),
    cursor: disabled ? 'not-allowed' : onClick || onTitleClick ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'background 0.1s ease',
    ...style,
  };

  // Handle row click: use onClick if provided, otherwise fall back to onTitleClick
  // This makes the entire row clickable, not just the title
  const handleRowClick = disabled ? undefined : (onClick || onTitleClick);

  return (
    <div
      className={`universal-row ${className}`}
      style={rowStyle}
      onClick={handleRowClick}
      onDoubleClick={disabled ? undefined : onDoubleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-id={id}
      data-done={done}
      data-selected={selected}
    >
      {/* LEFT SLOT - checkbox, indicator, etc. */}
      {leftSlot && (
        <div
          style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}
          onClick={(e) => e.stopPropagation()}
        >
          {leftSlot}
        </div>
      )}

      {/* ICON SLOT */}
      {icon && (
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          {icon}
        </div>
      )}

      {/* CONTENT AREA - expands to fill space */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {/* Title */}
        <div
          style={{
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: done ? 'var(--text-muted, #9ca3af)' : 'var(--text, #1f2937)',
            textDecoration: done ? 'line-through' : 'none',
            cursor: onTitleClick ? 'pointer' : 'inherit',
          }}
          onClick={(e) => {
            if (onTitleClick) {
              e.stopPropagation();
              onTitleClick();
            }
          }}
        >
          {title}
        </div>

        {/* Subtitle */}
        {subtitle && (
          <div
            style={{
              fontSize: '0.9em',
              color: 'var(--text-muted, #6b7280)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {subtitle}
          </div>
        )}

        {/* Meta content */}
        {meta && (
          <div style={{ fontSize: '0.85em', color: 'var(--text-muted, #6b7280)' }}>
            {meta}
          </div>
        )}
      </div>

      {/* DATE SLOT */}
      {formattedDate && (
        <div
          style={{
            flexShrink: 0,
            fontSize: '0.85em',
            color: 'var(--text-muted, #9ca3af)',
            whiteSpace: 'nowrap',
          }}
        >
          {formattedDate}
        </div>
      )}

      {/* STATUS DOT */}
      {status && (
        <div
          style={{
            flexShrink: 0,
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: STATUS_COLORS[status],
            boxShadow: `0 0 0 2px ${STATUS_COLORS[status]}20`,
          }}
          title={status}
        />
      )}

      {/* RIGHT SLOT - actions, buttons, etc. */}
      {rightSlot && (
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            opacity: hovered ? 1 : 0.5,
            transition: 'opacity 0.15s',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {rightSlot}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function getBackground(
  emphasis: 'default' | 'subtle' | 'highlighted',
  selected: boolean,
  hovered: boolean,
  done: boolean,
  disabled: boolean
): string {
  if (disabled) return 'var(--bg-disabled, #f9fafb)';
  if (selected) return 'var(--bg-selected, #eff6ff)';
  if (done) return 'var(--bg-done, #fafafa)';
  if (hovered) return 'var(--bg-hover, #f5f7fa)';

  switch (emphasis) {
    case 'subtle':
      return 'var(--bg-subtle, #fafafa)';
    case 'highlighted':
      return 'var(--bg-highlight, #fef3c7)';
    default:
      return 'var(--bg-row, #ffffff)';
  }
}

function formatDate(date: string | Date | ReactNode | undefined): ReactNode {
  if (!date) return null;

  // If it's already a ReactNode, return as-is
  if (React.isValidElement(date)) return date;

  // If it's a string that's not ISO format, return as-is
  if (typeof date === 'string' && !date.includes('-') && !date.includes('/')) {
    return date;
  }

  // Parse and format date - show as stored (UTC) without timezone conversion
  try {
    const d = typeof date === 'string' ? new Date(date) : date as Date;
    const now = new Date();

    // Extract UTC components directly (no timezone conversion)
    const day = String(d.getUTCDate()).padStart(2, '0');
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const hours = String(d.getUTCHours()).padStart(2, '0');
    const minutes = String(d.getUTCMinutes()).padStart(2, '0');

    // Same year: "04/02 19:15"
    if (d.getUTCFullYear() === now.getUTCFullYear()) {
      return `${day}/${month} ${hours}:${minutes}`;
    }

    // Different year: "04/02/26 19:15"
    const year = String(d.getUTCFullYear()).slice(-2);
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return date as ReactNode;
  }
}

// =============================================================================
// CONVENIENCE SUB-COMPONENTS
// =============================================================================

/**
 * Checkbox slot component
 * Use in leftSlot for selectable items
 */
export function RowCheckbox({
  checked,
  onChange,
  indeterminate = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  indeterminate?: boolean;
}) {
  return (
    <input
      type="checkbox"
      checked={checked}
      ref={(el) => el && (el.indeterminate = indeterminate)}
      onChange={(e) => onChange(e.target.checked)}
      style={{
        width: '16px',
        height: '16px',
        cursor: 'pointer',
        accentColor: 'var(--brand, #8b5cf6)',
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

/**
 * Status badge component
 * Use in meta slot for status labels
 */
export function RowBadge({
  text,
  color = 'neutral',
}: {
  text: string;
  color?: StatusLevel | string;
}) {
  const bgColor = STATUS_COLORS[color as StatusLevel] || color;
  return (
    <span
      style={{
        display: 'inline-flex',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '10px',
        fontWeight: 600,
        background: `${bgColor}20`,
        color: bgColor,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
      }}
    >
      {text}
    </span>
  );
}

/**
 * Meta items separator
 * Use between meta elements
 */
export function MetaSeparator() {
  return <span style={{ color: '#d1d5db', margin: '0 4px' }}>·</span>;
}

// =============================================================================
// PRIMARY ACTION COMPONENT - Contextual quick action for each row type
// =============================================================================

/**
 * Primary action types - the main quick action for each entity type
 *
 * RULE: Every row MUST have a primary action icon that provides
 * immediate control based on the entity's purpose:
 *
 * - check:   Completable items (Task, Milestone, Reminder, DueDate)
 * - eye:     Items to acknowledge/view (Call, Notification, Alert)
 * - archive: Items to hide/ignore (Email, Message, Conversation)
 * - star:    Items to favorite/remember (Contact, Invoice, Project)
 */
export type PrimaryActionType = 'check' | 'eye' | 'archive' | 'star';

/**
 * Mapping from entity prefix to primary action type
 */
export const PRIMARY_ACTION_MAP: Record<string, PrimaryActionType> = {
  // Completable items → check
  'tsk': 'check',    // Task
  'mst': 'check',    // Milestone
  'rem': 'check',    // Reminder
  'due': 'check',    // DueDate

  // Acknowledge items → eye
  'cal': 'eye',      // Call
  'ntf': 'eye',      // Notification
  'alt': 'eye',      // Alert

  // Hide/ignore items → archive
  'eml': 'archive',  // Email
  'msg': 'archive',  // Message
  'cnv': 'archive',  // Conversation

  // Favorite items → star
  'cnt': 'star',     // Contact
  'inv': 'star',     // Invoice
  'prj': 'star',     // Project
  'mov': 'star',     // Movement
  'evt': 'star',     // Event (can be starred for quick access)
};

/**
 * Get primary action type from entity ID
 */
export function getPrimaryActionType(entityId: string): PrimaryActionType | null {
  const prefix = entityId.split('_')[0] || entityId.substring(0, 3);
  return PRIMARY_ACTION_MAP[prefix] || null;
}

/**
 * PrimaryAction component
 * Renders the appropriate quick action icon based on entity type
 */
export function PrimaryAction({
  type,
  active = false,
  onChange,
  size = 18,
  disabled = false,
}: {
  type: PrimaryActionType;
  active?: boolean;
  onChange?: (newState: boolean) => void;
  size?: number;
  disabled?: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const actionConfig = usePrimaryActionConfig();

  const config = actionConfig[type];
  const activeColor = config.activeColor;
  const inactiveColor = '#d1d5db';
  const hoverColor = config.hoverColor;

  const currentColor = active
    ? activeColor
    : hovered
      ? hoverColor
      : inactiveColor;

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled && onChange) {
          onChange(!active);
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      disabled={disabled}
      title={active ? config.activeLabel : config.inactiveLabel}
      style={{
        background: 'none',
        border: 'none',
        padding: '2px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: disabled ? 0.4 : 1,
        transition: 'transform 0.1s, color 0.15s',
        transform: hovered && !disabled ? 'scale(1.1)' : 'scale(1)',
        color: currentColor,
      }}
    >
      <svg
        width={size}
        height={size}
        fill={type === 'star' && active ? currentColor : 'none'}
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d={active ? config.activeIcon : config.icon}
        />
      </svg>
    </button>
  );
}

// Base primary action config - labels will be translated per-component
const PRIMARY_ACTION_BASE_CONFIG: Record<PrimaryActionType, {
  icon: string;
  activeIcon: string;
  activeColor: string;
  hoverColor: string;
  activeLabelKey: string;
  inactiveLabelKey: string;
}> = {
  check: {
    // Empty circle → Checked circle
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    activeIcon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    activeColor: '#22c55e',
    hoverColor: '#86efac',
    activeLabelKey: 'common.completed',
    inactiveLabelKey: 'common.markAsCompleted',
  },
  eye: {
    // Eye → Eye with slash
    icon: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
    activeIcon: 'M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21',
    activeColor: '#6b7280',
    hoverColor: '#9ca3af',
    activeLabelKey: 'common.seen',
    inactiveLabelKey: 'common.markAsSeen',
  },
  archive: {
    // Archive box
    icon: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4',
    activeIcon: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4',
    activeColor: '#6b7280',
    hoverColor: '#9ca3af',
    activeLabelKey: 'common.archived',
    inactiveLabelKey: 'common.archive',
  },
  star: {
    // Star outline → Star filled
    icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
    activeIcon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
    activeColor: '#f59e0b',
    hoverColor: '#fcd34d',
    activeLabelKey: 'common.favorite',
    inactiveLabelKey: 'common.addToFavorites',
  },
};

// Hook to get translated primary action config
function usePrimaryActionConfig() {
  const { t } = useTranslation();
  return useMemo(() => {
    const config: Record<PrimaryActionType, {
      icon: string;
      activeIcon: string;
      activeColor: string;
      hoverColor: string;
      activeLabel: string;
      inactiveLabel: string;
    }> = {} as any;
    for (const [key, value] of Object.entries(PRIMARY_ACTION_BASE_CONFIG)) {
      config[key as PrimaryActionType] = {
        icon: value.icon,
        activeIcon: value.activeIcon,
        activeColor: value.activeColor,
        hoverColor: value.hoverColor,
        activeLabel: t(value.activeLabelKey),
        inactiveLabel: t(value.inactiveLabelKey),
      };
    }
    return config;
  }, [t]);
}

// =============================================================================
// FAST ACTIONS SLOT COMPONENT
// =============================================================================

export type FastActionType =
  | 'call'
  | 'videocall'
  | 'email'
  | 'message'
  | 'task'
  | 'event'
  | 'file'
  | 'invoice'
  | 'note'
  | 'reminder'
  | 'connect';

// Base fast action config - labels will be translated per-component
const FAST_ACTION_BASE_CONFIG: Record<
  FastActionType,
  { icon: string; labelKey: string; color: string }
> = {
  call: {
    icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
    labelKey: 'actions.calls',
    color: '#16a34a',
  },
  videocall: {
    icon: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z',
    labelKey: 'actions.videocalls',
    color: '#8b5cf6',
  },
  email: {
    icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    labelKey: 'actions.emails',
    color: '#3b82f6',
  },
  message: {
    icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
    labelKey: 'actions.messages',
    color: '#22c55e',
  },
  task: {
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
    labelKey: 'actions.tasks',
    color: '#f59e0b',
  },
  event: {
    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    labelKey: 'actions.events',
    color: '#ec4899',
  },
  file: {
    icon: 'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13',
    labelKey: 'actions.files',
    color: '#6b7280',
  },
  invoice: {
    icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    labelKey: 'actions.invoices',
    color: '#0891b2',
  },
  note: {
    icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
    labelKey: 'actions.notes',
    color: '#a855f7',
  },
  reminder: {
    icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
    labelKey: 'actions.reminders',
    color: '#ef4444',
  },
  connect: {
    icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
    labelKey: 'actions.connect',
    color: '#06b6d4',
  },
};

// Hook to get translated fast action config
function useFastActionConfig() {
  const { t } = useTranslation();
  return useMemo(() => {
    const config: Record<FastActionType, { icon: string; label: string; color: string }> = {} as any;
    for (const [key, value] of Object.entries(FAST_ACTION_BASE_CONFIG)) {
      config[key as FastActionType] = {
        icon: value.icon,
        color: value.color,
        label: t(value.labelKey),
      };
    }
    return config;
  }, [t]);
}

/**
 * FastActions component for rightSlot
 *
 * Shows a lightning bolt (Zap) trigger that reveals all fast actions on hover/click.
 * This is the STANDARD component for fast actions - use it everywhere.
 */
export function FastActions({
  actions,
  counts = {},
  onAction,
}: {
  actions: FastActionType[];
  counts?: Partial<Record<FastActionType, number>>;
  onAction?: (action: FastActionType) => void;
}) {
  const { t } = useTranslation();
  const fastActionConfig = useFastActionConfig();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Count total linked actions for badge
  const totalLinked = Object.values(counts).reduce((sum, c) => sum + (c || 0), 0);

  return (
    <div
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {/* Trigger button (lightning bolt) */}
      <button
        ref={triggerRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        title={t('actions.quickActions')}
        style={{
          position: 'relative',
          padding: '4px',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: isOpen ? 'var(--primary, #3b82f6)' : 'var(--text-muted, #9ca3af)',
          display: 'flex',
          alignItems: 'center',
          borderRadius: '4px',
          transition: 'color 0.15s ease',
        }}
      >
        <Zap size={17} fill={isOpen ? 'currentColor' : 'none'} />
        {/* Badge for total linked */}
        {totalLinked > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-2px',
              right: '-2px',
              minWidth: '12px',
              height: '12px',
              padding: '0 2px',
              borderRadius: '6px',
              background: 'var(--primary, #3b82f6)',
              color: '#fff',
              fontSize: '8px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {totalLinked > 9 ? '9+' : totalLinked}
          </span>
        )}
      </button>

      {/* Dropdown with actions */}
      {isOpen && (
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: '4px',
            display: 'flex',
            flexDirection: 'row',
            gap: '2px',
            padding: '6px 8px',
            backgroundColor: 'var(--surface, #fff)',
            border: '1px solid var(--border, #e5e7eb)',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 100,
            whiteSpace: 'nowrap',
          }}
        >
          {actions.map((action) => {
            const config = fastActionConfig[action];
            if (!config) return null;
            const count = counts[action] || 0;
            const isLinked = count > 0;

            return (
              <FastActionButton
                key={action}
                icon={config.icon}
                label={config.label}
                color={config.color}
                count={count}
                isLinked={isLinked}
                onClick={() => {
                  onAction?.(action);
                  setIsOpen(false);
                }}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function FastActionButton({
  icon,
  label,
  color,
  count,
  isLinked,
  onClick,
}: {
  icon: string;
  label: string;
  color: string;
  count: number;
  isLinked: boolean;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={`${label}${count > 0 ? ` (${count})` : ''}`}
      style={{
        position: 'relative',
        background: hovered ? '#f3f4f6' : 'transparent',
        border: 'none',
        padding: '4px',
        borderRadius: '4px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: isLinked ? color : '#9ca3af',
        transition: 'all 0.1s',
      }}
    >
      {/* Counter badge */}
      {count > 0 && (
        <span
          style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            minWidth: '14px',
            height: '14px',
            padding: '0 3px',
            borderRadius: '7px',
            background: color,
            color: '#fff',
            fontSize: '9px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
          }}
        >
          {count > 99 ? '99+' : count}
        </span>
      )}

      {/* Icon - size increased by 20% */}
      <svg
        width="17"
        height="17"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d={icon}
        />
      </svg>
    </button>
  );
}
